'use client';

import React, { useEffect } from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { useResponsive } from '@depop/web-ui-kit/useResponsive';
import { useTranslations } from 'next-intl';

import { MoreSellerItemsCard } from './MoreSellerItemsCard';
import styles from './styles.module.css';

import { ExtendedLink } from '@/components/ExtendedLink';
import { ModuleHeader, ModuleHeaderSize } from '@/components/ModuleHeader';
import { ProductImpressionHandler } from '@/components/ProductImpressionHandler';
import { ActivityTrackerEventType } from '@/modules/activityTracker/constants.ts';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker.ts';
import { toGuestLikeProduct } from '@/modules/guestLikes/helpers';
import { ImpressionsBatcherProvider } from '@/modules/impressions/ImpressionsBatcherProvider';
import { ModuleName } from '@/modules/impressions/types';
import { getProductPreview } from '@/modules/pictures/helpers/getProductPreview';
import { picturePreviewAdapter } from '@/modules/pictures/helpers.ts';
import {
  type MoreItemsProduct,
  type ProductBySlugResponse,
} from '@/modules/product/aliases';
import { PRODUCT_INTERACTION_TYPES } from '@/modules/product/constants.ts';
import { composeProductImageDescription } from '@/modules/product/helpers/composeProductImageDescription';
import { useProductTracking } from '@/modules/product/hooks/useProductTracking.ts';

type Props = {
  product: ProductBySlugResponse;
  moreItems: MoreItemsProduct[];
};

export function ProductModuleGrid({ moreItems, product }: Props) {
  const {
    id: productId,
    seller: { username: sellerUsername },
  } = product;
  const { isMinMedium } = useResponsive();
  const { sendProductInteractionActionATEvent } = useProductTracking();
  const { sendActivityTrackerEvent } = useActivityTracker();
  const tProducts = useTranslations('product');
  const tCommon = useTranslations('common');

  function handleProductClick() {
    sendProductInteractionActionATEvent(
      productId,
      PRODUCT_INTERACTION_TYPES.SELLER_ITEMS
    );
  }

  function handleSeeMoreClick() {
    sendProductInteractionActionATEvent(
      productId,
      PRODUCT_INTERACTION_TYPES.SELLER_SEE_MORE
    );
  }

  function getHoverPicture({ pictures }: MoreItemsProduct) {
    return isMinMedium && pictures && pictures.length > 1
      ? picturePreviewAdapter(pictures[1])
      : undefined;
  }

  useEffect(() => {
    if (moreItems.length) {
      sendActivityTrackerEvent(ActivityTrackerEventType.SellerMoreItemsView, {
        productId,
        itemCount: moreItems.length,
        items: moreItems.map(({ id }) => id),
      });
    }
  }, [productId, moreItems.length]);

  return (
    <ImpressionsBatcherProvider
      batchLocationId="pdpMoreSellerItems"
      impressionsType="productImpressions"
      eventType={ActivityTrackerEventType.PRODUCT_IMPRESSIONS}
      batchLimit={moreItems.length}
    >
      <section>
        <ModuleHeader
          title={tProducts('MoreItemsTitle')}
          size={ModuleHeaderSize.Small}
        >
          <ExtendedLink
            href={`/${sellerUsername}`}
            onClick={handleSeeMoreClick}
          >
            <Body variant="sm-regular" className={styles.seeMoreText}>
              {tCommon('SeeMore')}
            </Body>
          </ExtendedLink>
        </ModuleHeader>

        <ul className={styles.productGrid}>
          {moreItems.map((moreItem, index) => (
            <li key={moreItem.id} className={styles.listItem}>
              <ProductImpressionHandler
                index={index}
                isLastItem={index === moreItems.length - 1}
                pageNumber={1}
                moduleName={ModuleName.MoreFromThisSeller}
              >
                <MoreSellerItemsCard
                  id={moreItem.id}
                  slug={moreItem.slug}
                  pricing={moreItem.pricing}
                  displayPrice={moreItem.pricing.display_price}
                  sizes={moreItem.sizes}
                  preview={getProductPreview(moreItem)}
                  isLiked={moreItem.is_liked}
                  brandName={moreItem.brand_name}
                  hoverPicture={getHoverPicture(moreItem)}
                  sellerUsername={sellerUsername}
                  handleClick={handleProductClick}
                  guestLikeProduct={toGuestLikeProduct({
                    ...moreItem,
                    imageAlt: composeProductImageDescription({
                      brandName: moreItem.brand_name,
                      brandId: moreItem.brand_id,
                      attributes: moreItem.attributes,
                    }),
                  })}
                />
              </ProductImpressionHandler>
            </li>
          ))}
        </ul>
      </section>
    </ImpressionsBatcherProvider>
  );
}
